/**
 * Casamento no tempo entre os quadros do loop (satélite, de 10 em 10 min) e
 * as varreduras de cada radar — funções puras, testadas em `tempo.test.ts`.
 *
 * O problema: cada radar do SIPAM tem o próprio relógio. Medido em
 * 2026-09-21, nas 48 h anteriores:
 *
 * | Radar                 | Cadência                                   |
 * |-----------------------|--------------------------------------------|
 * | sbbe, sbsl, sbsn, sbtt | 10 min, nos minutos cheios (Belém com +5 s) |
 * | sbcz                  | 12 min (:00, :12, :24, :36, :48)            |
 * | sbmn                  | 12 min, mas SEM o :00 — um buraco de 24 min por hora |
 * | sbbv, sbpv, sbua      | 10–11 min, com deriva (:01, :11, :04, :14…) |
 *
 * E o GOES-19 tem quadros em :00, :10, :20… Não dá para exigir o mesmo
 * instante.
 *
 * A regra: para cada quadro, a varredura MAIS PRÓXIMA no tempo, para frente
 * ou para trás, desde que a diferença não passe de 12 min; no empate, a
 * anterior (o que já tinha acontecido no instante da imagem). Sem varredura
 * nessa distância, o radar some naquele quadro — nunca mostramos uma
 * varredura velha com o horário novo.
 *
 * Por que a mais próxima, e não "a última antes do quadro" (como as camadas
 * sobrepostas do satélite)? Porque o que se quer é comparar o eco com a nuvem
 * da mesma hora: com o radar de 12 min a regra "anterior" chega a 11 min de
 * atraso; a mais próxima nunca passa de 6.
 *
 * Por que 12 min? É o menor limite em que nenhum radar pisca à toa:
 * - radar de 10 min: a diferença máxima é 5 min, e 10 quando falta UMA
 *   varredura (buraco de 20 min, que acontece dezenas de vezes por dia);
 * - Manaus: o buraco sistemático de 24 min cai exatamente em 12 — com 10 min
 *   de tolerância o radar de Manaus piscaria uma vez por hora na animação;
 * - uma falha de verdade (≥ 25 min sem varredura) deixa o quadro do meio
 *   sem radar, que é o certo.
 * O eco anda 5–10 km em 12 min, menos que a nuvem que o contém.
 */
import { MINUTE_MS } from '../lib/time';
import { lastIndexAtOrBefore } from '../loop/frames';

/** Maior distância (para frente ou para trás) entre o quadro e a varredura exibida. */
export const TOLERANCIA_MS = 12 * MINUTE_MS;

/**
 * Índice (em `varreduras`, crescente) da varredura a exibir no instante `t`,
 * ou −1 se não há nenhuma a até `tolerancia`.
 */
export function varreduraParaQuadro(
  varreduras: readonly number[],
  t: number,
  tolerancia: number = TOLERANCIA_MS,
): number {
  const antes = lastIndexAtOrBefore(varreduras, t);
  const depois = antes + 1 < varreduras.length ? antes + 1 : -1;
  const dAntes = antes >= 0 ? t - varreduras[antes] : Infinity;
  const dDepois = depois >= 0 ? varreduras[depois] - t : Infinity;
  // Empate: a anterior — ela já existia no instante da imagem.
  const i = dAntes <= dDepois ? antes : depois;
  const d = Math.min(dAntes, dDepois);
  return i >= 0 && d <= tolerancia ? i : -1;
}

/** Instante da varredura de cada quadro (`null` = radar some naquele quadro). */
export function casarQuadros(
  quadros: readonly number[],
  varreduras: readonly number[],
  tolerancia: number = TOLERANCIA_MS,
): (number | null)[] {
  return quadros.map((t) => {
    const i = varreduraParaQuadro(varreduras, t, tolerancia);
    return i >= 0 ? varreduras[i] : null;
  });
}

/** Mediana do intervalo entre varreduras consecutivas (ms), ou null com menos de 2. */
export function cadenciaMediana(varreduras: readonly number[]): number | null {
  if (varreduras.length < 2) return null;
  const d: number[] = [];
  for (let i = 1; i < varreduras.length; i++) d.push(varreduras[i] - varreduras[i - 1]);
  d.sort((a, b) => a - b);
  const m = d.length >> 1;
  return d.length % 2 ? d[m] : (d[m - 1] + d[m]) / 2;
}
